import React from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
	Divider,
	IconButton,
	Input,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography
} from '@mui/material';
import { amber } from '@mui/material/colors';

import { selectFlatNavigation } from 'app/store/fuse/navigationSlice';
import { updateUserShortcuts } from 'app/store/userSlice';
import { Icon } from 'app/main/components';

interface FuseShortcutsProps {
	className?: string;
	variant?: string;
}

function FuseShortcuts({ className, variant = 'horizontal' }: FuseShortcutsProps) {
	const dispatch = useDispatch<any>();
	const shortcuts = useSelector(({ user }: any) => user.data.shortcuts) || [];
	const navigation = useSelector<any, any[]>(selectFlatNavigation);

	const searchInputRef = React.useRef<HTMLInputElement>(null);
	const [addMenu, setAddMenu] = React.useState(null);
	const [searchText, setSearchText] = React.useState('');
	const [searchResults, setSearchResults] = React.useState<any[] | undefined>();
	const shortcutItems = shortcuts ? shortcuts.map(id => navigation.find(item => item.id === id)) : [];

	function addMenuClick(event) {
		setAddMenu(event.currentTarget);
	}

	function addMenuClose() {
		setAddMenu(null);
	}

	function search(ev) {
		const newSearchText = ev.target.value;
		setSearchText(newSearchText);

		if (newSearchText.length !== 0 && navigation) {
			setSearchResults(navigation.filter(item => item.title.toLowerCase().includes(newSearchText.toLowerCase())));
			return;
		}
		setSearchResults(undefined);
	}

	function toggleInShortcuts(id) {
		let newShortcuts = [...shortcuts];
		newShortcuts = newShortcuts.includes(id) ? newShortcuts.filter(_id => id !== _id) : [...newShortcuts, id];
		dispatch(updateUserShortcuts(newShortcuts));
	}

	function ShortcutMenuItem({ item, onToggle }) {
		return (
			<Link to={item.url} role="button">
				<MenuItem key={item.id}>
					<ListItemIcon className="min-w-40">
						{item.icon ? (
							<Icon icon={item.icon} />
						) : (
							<span className="text-center text-20 font-semibold uppercase">{item.title[0]}</span>
						)}
					</ListItemIcon>
					<ListItemText primary={item.title} />
					<IconButton
						onClick={ev => {
							ev.preventDefault();
							ev.stopPropagation();
							onToggle(item.id);
						}}
						size="large"
					>
						<Icon icon={shortcuts.includes(item.id) ? 'star' : 'star-border'} color="action" />
					</IconButton>
				</MenuItem>
			</Link>
		);
	}

	const container = {
		show: {
			transition: {
				staggerChildren: 0.1
			}
		}
	};

	const item = {
		hidden: { opacity: 0, scale: 0.6 },
		show: { opacity: 1, scale: 1 }
	};

	return (
		<div className={clsx('flex flex-1', variant === 'vertical' && 'shrink grow-0 flex-col', className)}>
			<motion.div
				variants={container}
				initial="hidden"
				animate="show"
				className={clsx('flex flex-1', variant === 'vertical' && 'flex-col')}
			>
				{shortcutItems.map(
					_item =>
						_item && (
							<Link to={_item.url} key={_item.id} role="button">
								<Tooltip title={_item.title} placement={variant === 'horizontal' ? 'bottom' : 'left'}>
									<IconButton
										className="h-40 w-40 p-0"
										component={motion.div}
										variants={item}
										size="large"
									>
										{_item.icon ? (
											<Icon icon={_item.icon} />
										) : (
											<span className="text-20 font-semibold uppercase">{_item.title[0]}</span>
										)}
									</IconButton>
								</Tooltip>
							</Link>
						)
				)}

				<Tooltip title="Click to add/remove shortcut" placement={variant === 'horizontal' ? 'bottom' : 'left'}>
					<IconButton
						component={motion.div}
						variants={item}
						className="h-40 w-40 p-0"
						aria-owns={addMenu ? 'add-menu' : null}
						aria-haspopup="true"
						onClick={addMenuClick}
						size="large"
					>
						<Icon icon="star" color={amber[600]} />
					</IconButton>
				</Tooltip>
			</motion.div>

			<Menu
				id="add-menu"
				anchorEl={addMenu}
				open={Boolean(addMenu)}
				onClose={addMenuClose}
				classes={{
					paper: 'mt-48 min-w-256'
				}}
				TransitionProps={{
					onEntered: () => {
						searchInputRef.current?.focus();
					},
					onExited: () => {
						setSearchText('');
					}
				}}
			>
				<div className="p-16 pt-8">
					<Input
						inputRef={searchInputRef}
						value={searchText}
						onChange={search}
						placeholder="Search for an app or page"
						className=""
						fullWidth
						inputProps={{
							'aria-label': 'Search'
						}}
						disableUnderline
					/>
				</div>

				<Divider />

				{searchText.length !== 0 &&
					searchResults &&
					searchResults.map(_item => (
						<ShortcutMenuItem key={_item.id} item={_item} onToggle={() => toggleInShortcuts(_item.id)} />
					))}

				{searchText.length !== 0 && (!searchResults || searchResults?.length === 0) && (
					<Typography color="textSecondary" className="p-16 pb-8">
						No results..
					</Typography>
				)}

				{searchText.length === 0 &&
					shortcutItems.map(
						_item =>
							_item && (
								<ShortcutMenuItem
									key={_item.id}
									item={_item}
									onToggle={() => toggleInShortcuts(_item.id)}
								/>
							)
					)}
			</Menu>
		</div>
	);
}

export default React.memo(FuseShortcuts);
